'use client';

import { useState } from 'react';

import { AddToCart } from '@/features/add-to-cart/client';
import { BuyTheLook } from '@/features/buy-the-look/client';
import { SizeAdvisor } from '@/features/size-advisor/client';
import { findVariant, VariantPicker, type Product, type Selection } from '@/entities/product';
import { formatMoney } from '@/shared/lib/money';
import { BONUS_FORMS, pluralize } from '@/shared/lib/plural';
import { Breadcrumbs } from '@/shared/ui/breadcrumbs';
import { Chip } from '@/shared/ui/chip';
import { Popover, PopoverClose } from '@/shared/ui/popover';
import { TextButton, TextLink } from '@/shared/ui/text-link';

import styles from './product-purchase.module.scss';

export type ProductPurchaseProps = {
  product: Product;
  looks: Product[];
  selection: Selection;
  tip: string;
  onColorChange: (colorId: string) => void;
  onSizeChange: (size: string) => void;
  onTip: (tip: string) => void;
  initialFitOpen: boolean;
};

export function ProductPurchase({
  product,
  looks,
  selection,
  tip,
  onColorChange,
  onSizeChange,
  onTip,
  initialFitOpen,
}: ProductPurchaseProps) {
  const [fitOpen, setFitOpen] = useState(initialFitOpen && product.sizeAdvice);
  const variant = findVariant(product, selection);
  const price = variant?.price ?? product.price;
  const preorder = variant?.availability === 'preorder';
  const bonus = price ? product.bonusPoints : null;
  const installment = product.installment;
  return (
    <aside className={styles.root} aria-label="Покупка">
      <Breadcrumbs
        items={[
          { label: 'Главная', href: '/' },
          { label: 'Каталог', href: '/catalog' },
          {
            label: product.categoryLabel,
            href: `/catalog?direction=${product.direction}`,
          },
          { label: product.name },
        ]}
      />
      <h1 className={styles.title}>{product.name}</h1>
      <div className={styles.price}>
        <span className={styles.amount}>{price ? formatMoney(price) : 'Цена уточняется'}</span>
        {bonus !== null && bonus > 0 && (
          <span className={styles.bonus}>
            <span>
              +{bonus} {pluralize(bonus, BONUS_FORMS)} за покупку
            </span>
            <Popover
              label="О бонусах"
              theme="editorial"
              sideOffset={12}
              trigger={
                <button type="button" className={styles.question} aria-label="Что такое бонусы">
                  ?
                </button>
              }
            >
              <div className={styles.popover}>
                <div className={styles['popover-head']}>
                  <span>
                    Это бонусы программы лояльности J-VU. Они будут начислены после получения заказа
                  </span>
                  <PopoverClose className={styles.x} label="Закрыть">
                    ×
                  </PopoverClose>
                </div>
                <TextLink href="/account/bonuses" className={styles.start}>
                  Подробнее
                </TextLink>
              </div>
            </Popover>
          </span>
        )}
      </div>
      <VariantPicker
        idPrefix="pdp"
        product={product}
        colorId={selection.colorId}
        size={selection.size}
        onColorChange={onColorChange}
        onSizeChange={onSizeChange}
        tip={tip}
        className={styles.picker}
      />
      {product.sizeAdvice && (
        <TextButton className={styles.start} onClick={() => setFitOpen(true)}>
          Подобрать размер
        </TextButton>
      )}
      {preorder && (
        <div className={styles.preorder}>
          <span className={styles.badge}>Предзаказ</span>
          <span>
            Товар доступен по предзаказу.
            {variant?.shipDate ? ` Ожидаемая отгрузка: ${variant.shipDate}` : ''}
          </span>
        </div>
      )}
      <AddToCart product={product} selection={selection} onTip={onTip} />
      {installment && (
        <div className={styles.parts}>
          <span>
            Оплата частями · {installment.count} × {formatMoney(installment.amount)}
          </span>
          <div className={styles.chips}>
            <Chip appearance="outlined">[Сервис рассрочки 1]</Chip>
            <Chip appearance="outlined">[Сервис рассрочки 2]</Chip>
          </div>
        </div>
      )}
      {looks.length > 0 && (
        <BuyTheLook
          product={product}
          looks={looks}
          selection={selection}
          onColorChange={onColorChange}
          onSizeChange={onSizeChange}
        />
      )}
      {product.sizeAdvice && (
        <SizeAdvisor
          product={product}
          open={fitOpen}
          onOpenChange={setFitOpen}
          onSelectSize={onSizeChange}
        />
      )}
    </aside>
  );
}
